// Admin App Launcher — 4×4 grid of mini-apps and admin tools.
//
// Source-of-truth reference: /var/www/kitchenos/admin-standalone.html
//   panel:    ~line 1471 (#app-launcher overlay)
//   app list: ~line 2101 (AL_APPS) — 17 entries by inventory; we surface
//             the 16 most useful as a 4×4 grid (the "Sign Out" entry is
//             handled by the chrome, not by the launcher in this portal).
//
// Each tile either:
//   - navigates within the admin SPA via ctx.navigate(...), or
//   - opens an external/legacy admin tool in a new tab (target="_blank").
//
// The endpoint-/page-backed tools below that don't yet exist (audit log,
// fraud dashboard, etc.) are marked "Coming soon" — the tile is rendered but
// the action shows a toast instead of navigating into a 404.

import { registerPage } from "../../shell/router";
import type { Page, PageContext, PageCleanup } from "../../shell/types";

import "./styles.css";

// ── Types ────────────────────────────────────────────────────────────────────

type AppCategory = "core" | "platform" | "tools" | "settings";

interface LauncherApp {
  id: string;
  label: string;
  description: string;
  category: AppCategory;
  icon: string;
  /** Tailwind-ish accent colour for the tile. */
  color: string;
  bg: string;
  /** If set, opens this URL in a new tab. Mutually exclusive with `page`. */
  href?: string;
  /** If set, calls ctx.navigate(page). */
  page?: string;
  /** If true, shows a "soon" pill and does not navigate. */
  comingSoon?: boolean;
}

// ── App list (16 tiles for a 4×4 grid) ───────────────────────────────────────

const APPS: ReadonlyArray<LauncherApp> = [
  {
    id: "dashboard",
    label: "Dashboard",
    description: "Platform overview, headline KPIs and the day's queue.",
    category: "core",
    color: "#e58200",
    bg: "rgba(229,130,0,.12)",
    icon: '<rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>',
    page: "dashboard",
  },
  {
    id: "retailers",
    label: "Retailers",
    description: "Onboarded retailers, drill in, manage their account.",
    category: "core",
    color: "#22c55e",
    bg: "rgba(34,197,94,.12)",
    icon: '<path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>',
    page: "retailers",
  },
  {
    id: "suppliers",
    label: "Suppliers",
    description: "Supplier directory, trade portal access, catalogues.",
    category: "core",
    color: "#3b82f6",
    bg: "rgba(59,130,246,.12)",
    icon: '<path d="M16 3h5v5"/><line x1="21" y1="3" x2="14" y2="10"/><rect x="3" y="3" width="9" height="9"/><rect x="3" y="14" width="18" height="7"/>',
    page: "suppliers",
  },
  {
    id: "onboarding",
    label: "Onboarding",
    description: "In-flight retailer onboarding sessions.",
    category: "core",
    color: "#a855f7",
    bg: "rgba(168,85,247,.12)",
    icon: '<polyline points="20 6 9 17 4 12"/>',
    page: "onboarding",
  },
  {
    id: "analytics",
    label: "Analytics",
    description: "Platform-wide metrics, retention, revenue, traffic.",
    category: "platform",
    color: "#22c55e",
    bg: "rgba(34,197,94,.12)",
    icon: '<path d="M3 3v18h18"/><polyline points="7 14 11 10 14 13 20 7"/>',
    page: "analytics",
  },
  {
    id: "tasks",
    label: "Tasks",
    description: "Steve's personal admin todo list.",
    category: "core",
    color: "#eab308",
    bg: "rgba(234,179,8,.12)",
    icon: '<polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>',
    page: "tasks",
  },
  {
    id: "notes",
    label: "Notes",
    description: "Markdown-friendly personal notes.",
    category: "core",
    color: "#f97316",
    bg: "rgba(249,115,22,.12)",
    icon: '<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>',
    page: "notes",
  },
  {
    id: "events",
    label: "Events",
    description: "Platform event feed.",
    category: "platform",
    color: "#ec4899",
    bg: "rgba(236,72,153,.12)",
    icon: '<rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>',
    page: "events",
  },
  {
    id: "links",
    label: "Bookmarks",
    description: "Quick links to external trade tools.",
    category: "tools",
    color: "#0ea5e9",
    bg: "rgba(14,165,233,.12)",
    icon: '<path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>',
    page: "links",
  },
  {
    id: "feedback",
    label: "Feedback",
    description: "In-app feedback from retailers, customers, suppliers.",
    category: "platform",
    color: "#14b8a6",
    bg: "rgba(20,184,166,.12)",
    icon: '<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>',
    page: "feedback",
  },
  {
    id: "system-health",
    label: "System Health",
    description: "Live infra status and recent errors.",
    category: "platform",
    color: "#ef4444",
    bg: "rgba(239,68,68,.12)",
    icon: '<path d="M22 12h-4l-3 9L9 3l-3 9H2"/>',
    page: "system-health",
  },
  {
    id: "dispatch",
    label: "Dispatch (legacy)",
    description: "Driver dispatch — legacy standalone page.",
    category: "tools",
    color: "#6366f1",
    bg: "rgba(99,102,241,.12)",
    icon: '<rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/>',
    href: "/dispatch.html",
  },
  {
    id: "audit-log",
    label: "Audit Log",
    description: "Append-only audit trail viewer.",
    category: "tools",
    color: "#94a3b8",
    bg: "rgba(148,163,184,.12)",
    icon: '<path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>',
    href: "/admin/audit-log.html",
    comingSoon: true,
  },
  {
    id: "fraud",
    label: "Fraud Dashboard",
    description: "Risk flags and suspicious activity.",
    category: "tools",
    color: "#f43f5e",
    bg: "rgba(244,63,94,.12)",
    icon: '<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>',
    href: "/admin/fraud.html",
    comingSoon: true,
  },
  {
    id: "stripe-webhooks",
    label: "Stripe Webhooks",
    description: "Recent Stripe webhook events.",
    category: "tools",
    color: "#7c3aed",
    bg: "rgba(124,58,237,.12)",
    icon: '<path d="M18 16.98h-5.99c-1.66 0-3.01-1.34-3.01-3s1.34-3.01 3-3.01h11.99c1.66 0 3-1.34 3-3"/><path d="M6 7.02h6c1.66 0 3 1.34 3 3"/>',
    href: "/admin/stripe-webhooks.html",
    comingSoon: true,
  },
  {
    id: "ai-conversations",
    label: "AI Conversations",
    description: "Recent Jess (Ask-Steve) and Bobi conversations.",
    category: "tools",
    color: "#f59e0b",
    bg: "rgba(245,158,11,.12)",
    icon: '<path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>',
    href: "/admin/ai-conversations.html",
    comingSoon: true,
  },
];

const CATEGORIES: ReadonlyArray<{ value: AppCategory | "all"; label: string }> = [
  { value: "all", label: "All" },
  { value: "core", label: "Core" },
  { value: "platform", label: "Platform" },
  { value: "tools", label: "Tools" },
  { value: "settings", label: "Settings" },
];

// ── Helpers ──────────────────────────────────────────────────────────────────

function esc(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

// ── Page ─────────────────────────────────────────────────────────────────────

const appLauncherPage: Page = {
  name: "app-launcher",
  label: "App Launcher",
  icon: "📐",

  mount(container: HTMLElement, ctx: PageContext): PageCleanup {
    let category: AppCategory | "all" = "all";

    const root = document.createElement("section");
    root.className = "aapp-page";
    root.innerHTML = `
      <header class="aapp-header">
        <h1>Admin App Launcher</h1>
        <p>Quick access to every admin mini-app and tool.</p>
      </header>

      <div class="aapp-cats" role="tablist" data-role="cats">
        ${CATEGORIES.map(
          (c) =>
            `<button type="button" role="tab" class="aapp-cat${c.value === "all" ? " aapp-cat-active" : ""}" data-cat="${c.value}">${esc(c.label)}</button>`,
        ).join("")}
      </div>

      <div class="aapp-grid" data-role="grid"></div>

      <p class="aapp-foot">${APPS.length} apps · Tiles tagged "soon" are placeholders for tools that exist in inventory but aren't built yet.</p>
    `;
    container.appendChild(root);

    const grid = root.querySelector<HTMLDivElement>("[data-role='grid']")!;
    const catsEl = root.querySelector<HTMLDivElement>("[data-role='cats']")!;

    function render(): void {
      const apps = category === "all" ? APPS : APPS.filter((a) => a.category === category);
      grid.innerHTML = apps
        .map(
          (a) => `
        <div class="aapp-tile" data-id="${esc(a.id)}">
          <div class="aapp-ico" style="background:${a.bg};color:${a.color}">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              ${a.icon}
            </svg>
          </div>
          <div class="aapp-body">
            <div class="aapp-name">
              ${esc(a.label)}
              ${a.comingSoon ? `<span class="adm-pill aapp-soon">soon</span>` : ""}
            </div>
            <div class="aapp-desc">${esc(a.description)}</div>
            ${
              a.href
                ? `<a class="adm-btn aapp-open" href="${esc(a.href)}" ${a.comingSoon ? "" : "target=\"_blank\" rel=\"noopener noreferrer\""} data-role="open">Open</a>`
                : `<button class="adm-btn aapp-open" type="button" data-role="open">Open</button>`
            }
          </div>
        </div>`,
        )
        .join("");
    }

    const onCatClick = (e: Event): void => {
      const t = (e.target as HTMLElement).closest<HTMLButtonElement>(".aapp-cat");
      if (!t) return;
      const v = (t.dataset.cat as AppCategory | "all" | undefined) ?? "all";
      category = v;
      for (const btn of catsEl.querySelectorAll<HTMLButtonElement>(".aapp-cat")) {
        btn.classList.toggle("aapp-cat-active", btn.dataset.cat === v);
      }
      render();
    };

    const onGridClick = (e: Event): void => {
      const target = e.target as HTMLElement;
      const btn = target.closest<HTMLElement>("[data-role='open']");
      if (!btn) return;
      const tile = target.closest<HTMLElement>(".aapp-tile");
      const id = tile?.dataset.id;
      if (!id) return;
      const app = APPS.find((a) => a.id === id);
      if (!app) return;
      if (app.comingSoon) {
        e.preventDefault();
        ctx.showToast(`${app.label} — coming soon`, "info");
        return;
      }
      if (app.page) {
        e.preventDefault();
        ctx.navigate(app.page);
      }
      // For external hrefs the default anchor click handles it.
    };

    catsEl.addEventListener("click", onCatClick);
    grid.addEventListener("click", onGridClick);

    render();

    return () => {
      catsEl.removeEventListener("click", onCatClick);
      grid.removeEventListener("click", onGridClick);
      root.remove();
    };
  },
};

registerPage(appLauncherPage);
export default appLauncherPage;
